import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileText, 
  Download, 
  Calendar, 
  TrendingUp, 
  Users, 
  Camera, 
  Shield,
  AlertTriangle,
  BarChart3,
  PieChart,
  Filter,
  FileSpreadsheet
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { reportsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Reports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedSite, setSelectedSite] = useState('all');
  const [reportType, setReportType] = useState('security');
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportFormat, setReportFormat] = useState('pdf');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeGraphs, setIncludeGraphs] = useState(true);
  const { toast } = useToast();

  // Real data - will be loaded from API
  const [reports, setReports] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({
    totalIncidents: 0,
    resolvedIncidents: 0,
    avgResponseTime: '0 min',
    camerasUptime: '0%',
    accessAttempts: 0,
    unauthorizedAttempts: 0,
    activeSecurity: 0,
    sitesMonitored: 0
  });

  const handleGenerateReport = () => {
    setIsGenerateDialogOpen(true);
  };

  const handleDownloadReport = (reportId) => {
    console.log(`Téléchargement du rapport ${reportId}`);
  };

  const handleSubmitGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const reportData = {
        type: reportType,
        format: reportFormat,
        startDate: startDate,
        endDate: endDate,
        includeGraphs: includeGraphs
      };

      // Call API
      const response = await reportsAPI.generate(reportData);
      
      toast({
        title: "Rapport en cours de génération",
        description: "Vous recevrez une notification une fois le rapport prêt",
      });

      setIsGenerateDialogOpen(false);
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de générer le rapport",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Rapports et analyses</h2>
          <p className="text-gray-600">Génération et consultation des rapports de sécurité</p>
        </div>
        <Button onClick={handleGenerateReport} className="flex items-center space-x-2">
          <FileText className="w-4 h-4" />
          <span>Générer un rapport</span>
        </Button>
      </div>

      {/* Generate Report Dialog */}
      <Dialog open={isGenerateDialogOpen} onOpenChange={setIsGenerateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Générer un rapport</DialogTitle>
            <DialogDescription>
              Créez un rapport personnalisé en PDF ou Excel
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmitGenerate}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reportType">Type de rapport *</Label>
                <select
                  id="reportType"
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="security">Rapport de sécurité</option>
                  <option value="incidents">Rapport d'incidents</option>
                  <option value="attendance">Rapport de présence</option>
                  <option value="access">Rapport d'accès</option>
                  <option value="cameras">Rapport caméras</option>
                  <option value="alarms">Rapport alarmes</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Format d'export *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setReportFormat('pdf')}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center space-y-2 ${reportFormat === 'pdf' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                  >
                    <FileText className="w-8 h-8" />
                    <span className="font-medium">PDF</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportFormat('excel')}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center space-y-2 ${reportFormat === 'excel' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                  >
                    <FileSpreadsheet className="w-8 h-8" />
                    <span className="font-medium">Excel</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Date de début *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">Date de fin *</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeGraphs}
                    onChange={(e) => setIncludeGraphs(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span>Inclure les graphiques et visualisations</span>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Format {reportFormat === 'pdf' ? 'PDF' : 'Excel'} sélectionné</p>
                  <p>
                    {reportFormat === 'pdf' 
                      ? 'Le rapport sera généré au format PDF avec mise en page professionnelle'
                      : 'Le rapport sera généré au format Excel avec données exportables'}
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsGenerateDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Génération...' : 'Générer le rapport'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Incidents totaux</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{monthlyStats.totalIncidents}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">{monthlyStats.resolvedIncidents}</span> résolus
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Temps de réponse moyen</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{monthlyStats.avgResponseTime}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">-12%</span> vs mois dernier
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Disponibilité caméras</CardTitle>
            <Camera className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{monthlyStats.camerasUptime}</div>
            <p className="text-xs text-muted-foreground">
              234/267 caméras actives
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tentatives d'accès</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{monthlyStats.accessAttempts.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-red-600">{monthlyStats.unauthorizedAttempts}</span> non autorisées
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Report Generation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="w-5 h-5" />
            <span>Générer un nouveau rapport</span>
          </CardTitle>
          <CardDescription>
            Créez des rapports personnalisés selon vos critères
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger>
                <SelectValue placeholder="Type de rapport" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Rapport complet</SelectItem>
                <SelectItem value="security">Sécurité</SelectItem>
                <SelectItem value="incidents">Incidents</SelectItem>
                <SelectItem value="access">Contrôle d'accès</SelectItem>
                <SelectItem value="cameras">Surveillance caméras</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger>
                <SelectValue placeholder="Période" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Cette semaine</SelectItem>
                <SelectItem value="month">Ce mois</SelectItem>
                <SelectItem value="quarter">Ce trimestre</SelectItem>
                <SelectItem value="year">Cette année</SelectItem>
                <SelectItem value="custom">Période personnalisée</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger>
                <SelectValue placeholder="Site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les sites</SelectItem>
                <SelectItem value="1">Entrepôt Logistique A</SelectItem>
                <SelectItem value="2">Bureau Central Paris</SelectItem>
                <SelectItem value="3">Usine de Production</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={handleGenerateReport} className="w-full">
              <FileText className="w-4 h-4 mr-2" />
              Générer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Rapports récents</span>
          </CardTitle>
          <CardDescription>
            Vos derniers rapports générés
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {reports.map((report) => (
              <div key={report.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{report.title}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <Badge variant="outline">{report.type}</Badge>
                      <span className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{report.period}</span>
                      </span>
                      <span>Site: {report.site}</span>
                      <span>Taille: {report.size}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Badge variant="default">{report.status}</Badge>
                  <span className="text-sm text-gray-500">
                    {new Date(report.generatedDate).toLocaleDateString('fr-FR')}
                  </span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDownloadReport(report.id)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-5 h-5" />
              <span>Évolution des incidents</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-2" />
                <p>Graphique des incidents par mois</p>
                <p className="text-sm">(Intégration graphique à venir)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PieChart className="w-5 h-5" />
              <span>Répartition par type d'incident</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center text-gray-500">
                <PieChart className="w-12 h-12 mx-auto mb-2" />
                <p>Répartition des types d'incidents</p>
                <p className="text-sm">(Intégration graphique à venir)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Reports;